import PageWrapper from "@/components/PageWrapper/PageWrapper";
import { Stack } from "@mui/joy";
import IntroPanel from "./components/IntroPanel";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { getAgent } from "@/services/api/agents/agents";
import NotesSection from "./components/NotesSection";
import Loader from "@/components/Loader/Loader";
import ErrorFallback from "@/components/ErrorFallback/ErrorFallback";
import { useMemo, useRef, useState } from "react";
import ProfileTabs, { PROFILE_TABS_STICKY_TOP } from "./components/ProfileTabs";
import OverviewTab from "./components/OverviewTab";
import ExpertiseTab from "./components/ExpertiseTab";
import FieldGroupsTab from "./components/FieldGroupsTab";
import FieldSearchResults from "./components/FieldSearchResults";
import useAgentNotes from "./useAgentNotes";
import {
  LEGACY_TAB_VALUES,
  PROFILE_TABS,
  ProfileTabValue,
  getProfileFieldGroups,
  searchFieldGroups,
} from "./profileFields";

const resolveTab = (param: string | null): ProfileTabValue => {
  if (!param) return "overview";
  return LEGACY_TAB_VALUES[param] || PROFILE_TABS.find((tab) => tab.value === param)?.value || "overview";
};

function AgentProfile() {
  const [searchParams, setSearchParams] = useSearchParams();
  const email = searchParams.get("email") as string;
  const tab = resolveTab(searchParams.get("tab"));

  const [searchQuery, setSearchQuery] = useState("");
  const heroRef = useRef<HTMLDivElement>(null);

  const agentData = useQuery({
    queryKey: ["agent", email],
    queryFn: ({ queryKey }) => getAgent({ email: queryKey[1] }),
    enabled: !!email,
  });
  const { openNotes, overdueNotes } = useAgentNotes(email);

  const agent = agentData?.data?.agent || null;
  const groups = useMemo(() => getProfileFieldGroups(agent), [agent]);
  const searchResults = useMemo(() => searchFieldGroups(groups, searchQuery), [groups, searchQuery]);

  const openTab = (nextTab: ProfileTabValue) => {
    setSearchQuery("");
    setSearchParams(
      (prev) => {
        prev.set("tab", nextTab);
        return prev;
      },
      { replace: true },
    );

    // if scrolled into the content, bring the new tab's top into view under the sticky bar
    const hero = heroRef.current;
    if (hero) {
      const contentTop = hero.getBoundingClientRect().bottom + window.scrollY - PROFILE_TABS_STICKY_TOP;
      if (window.scrollY > contentTop) window.scrollTo({ top: contentTop, behavior: "smooth" });
    }
  };

  const renderTab = () => {
    if (searchQuery.trim()) {
      return (
        <FieldSearchResults
          groups={searchResults}
          query={searchQuery.trim()}
          onOpenTab={openTab}
          onClear={() => setSearchQuery("")}
        />
      );
    }

    switch (tab) {
      case "overview":
        return (
          <OverviewTab
            agent={agent}
            groups={groups}
            openNotes={openNotes}
            overdueNotes={overdueNotes}
            onOpenTab={openTab}
          />
        );
      case "expertise":
        return <ExpertiseTab agent={agent} />;
      case "notes":
        return <NotesSection email={email} />;
      default: {
        const groupIds = PROFILE_TABS.find((t) => t.value === tab)?.groupIds || [];
        return <FieldGroupsTab key={tab} groups={groupIds.map((id) => groups[id]).filter(Boolean)} />;
      }
    }
  };

  return (
    <Stack minHeight="80vh" sx={{ backgroundColor: "background.level1" }}>
      {agentData.isLoading ? (
        <Loader />
      ) : agentData.error ? (
        <ErrorFallback text="Couldn't load agent data" retry={agentData.refetch} />
      ) : (
        <>
          <div ref={heroRef}>
            <IntroPanel agent={agent} />
          </div>
          <ProfileTabs
            value={tab}
            onChange={openTab}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            openNotesCount={openNotes.length}
            name={[agent?.firstname, agent?.lastname].filter(Boolean).join(" ")}
            profilePicture={agent?.profilePicture}
            heroRef={heroRef}
          />
          <PageWrapper sx={{ pt: 3, px: { xs: 2, md: 4 } }}>{renderTab()}</PageWrapper>
        </>
      )}
    </Stack>
  );
}

export default AgentProfile;
